"use client";

import Image from "next/image";
import { Link } from "@/i18n/navigation";
import { useState, useEffect } from "react";
import { CustomButton } from "@/components/ui/custom-button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DownloadFormModal } from "@/components/modals/DownloadFormModal";
import { useTranslations } from "next-intl";

interface CaseStudyItem {
	title: string;
	text: string;
	imageUrl: string;
	link: string;
}

interface ContentDownloadProps {
	className?: string;
	caseStudies?: CaseStudyItem[];
	showImage?: boolean;
	showSingleButton?: boolean;
	buttonText?: string;
	buttonHref?: string;
	secondButtonText?: string;
	secondButtonHref?: string;
	compact?: boolean;
	title?: string;
	description?: string;
	imageSrc?: string;
	imageAlt?: string;
	onDownloadClick?: () => void;
}

export function ContentDownload({
	className = "",
	caseStudies = [],
	showImage = true,
	showSingleButton = false,
	buttonText = "Book a demo",
	buttonHref = "/book-a-demo",
	secondButtonText,
	secondButtonHref,
	compact = false,
	title,
	description,
	imageSrc,
	imageAlt,
	onDownloadClick
}: ContentDownloadProps) {
	const t = useTranslations();
	
	const [currentPage, setCurrentPage] = useState(0);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const itemsPerPage = 6;
	const totalPages = Math.ceil(caseStudies.length / itemsPerPage);
	const showPagination = caseStudies.length > itemsPerPage;

	const currentItems = caseStudies.slice(
		currentPage * itemsPerPage,
		(currentPage + 1) * itemsPerPage
	);

	const handlePrevPage = () => {
		setCurrentPage(prev => Math.max(0, prev - 1));
	};

	const handleNextPage = () => {
		setCurrentPage(prev => Math.min(totalPages - 1, prev + 1));
	};
	// Compact version for when used in case studies pagination
	if (compact) {
		return (
			<section className={`relative py-8 sm:py-10 md:py-12 bg-linear-to-l from-ss-light-green via-ss-green to-ss-blue rounded-2xl sm:rounded-3xl overflow-hidden ${className}`}>
				{/* Decorative SVG positioned on the right - matching banner format */}
				<div className="absolute top-0 right-0 w-full h-full pointer-events-none z-0 opacity-60 sm:opacity-80 md:opacity-100">
					<div className="relative w-full h-full flex items-start justify-end overflow-hidden">
						{showPagination ? (
							<div className="w-[300px] sm:w-[400px] md:w-[450px] top-[-100px] sm:top-[-150px] md:top-[-200px] left-[-50px] sm:left-[-75px] md:left-[-100px] transition-all duration-500 ease-in-out relative flex items-center justify-center h-full">
								<div className="flex items-center gap-3 sm:gap-4 md:gap-6" style={{ transform: 'scale(0.4) translateX(0%) sm:scale(0.5) md:scale(0.6)' }}>
									<button
										onClick={handlePrevPage}
										disabled={currentPage === 0}
										className="p-2 sm:p-3 md:p-4 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 text-white hover:bg-white/30 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
										aria-label="Previous page"
									>
										<ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8" />
									</button>

									<div className="text-white text-center">
										<div className="text-sm sm:text-base md:text-lg font-semibold">
											{currentPage + 1} / {totalPages}
										</div>
									</div>

									<button
										onClick={handleNextPage}
										disabled={currentPage === totalPages - 1}
										className="p-2 sm:p-3 md:p-4 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 text-white hover:bg-white/30 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
										aria-label="Next page"
									>
										<ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8" />
									</button>
								</div>
							</div>
						) : (
							<div className="w-[300px] sm:w-[380px] md:w-[430px] top-[-120px] sm:top-[-170px] md:top-[-215px] left-[-50px] sm:left-[-75px] md:left-[-100px] transition-all duration-500 ease-in-out relative">
								<img
									src="/two_lines.svg"
									alt="Illustration of the StudyStash teaching-learning loop"
									className="w-full h-full object-contain transition-all duration-500 ease-in-out"
									style={{
										transform: 'scale(0.4) translateX(0%) sm:scale(0.5) md:scale(0.6)'
									}}
								/>
							</div>
						)}
					</div>
				</div>

				<div className="max-w-4xl ml-0 mr-auto px-4 sm:px-6 lg:px-8 relative z-10">
					<div className="text-white">
						<h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4 leading-tight text-white">
							Empower Your Students to Succeed
						</h2>

						<p className="text-sm sm:text-base lg:text-lg mb-4 sm:mb-6 leading-relaxed opacity-90 text-white">
							Give your students the {t("general.words.personalised")} support they need to thrive.
							StudyStash helps boost engagement, confidence, and
							outcomes-schedule a demo to see it in action.
						</p>

						{/* Buttons */}
						<div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 pt-4 sm:pt-6 md:pt-8">
							{showSingleButton ? (
								<Link href={buttonHref} className="inline-flex">
									<CustomButton variant="primary" size="md">
										{buttonText}
									</CustomButton>
								</Link>
							) : (
								<>
									<Link href={buttonHref} className="inline-flex">
										<CustomButton variant="primary" size="md">
											{buttonText}
										</CustomButton>
									</Link>
									<CustomButton
										variant="outline"
										size="md"
										onClick={() => setIsModalOpen(true)}
										className="inline-flex"
									>
										Download the report
									</CustomButton>
								</>
							)}
						</div>
					</div>
				</div>

				{/* Case Studies Display for compact version */}
				{showPagination && currentItems.length > 0 && (
					<div className="mt-6 sm:mt-8 md:mt-12 px-4 sm:px-6 lg:px-8">
						<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
							{currentItems.map((item, index) => (
								<Link
									key={index}
									href={item.link}
									className="group block h-full"
								>
									<div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4 transition-all duration-300 ease-in-out group-hover:scale-[1.02] group-hover:bg-white/20 h-full flex flex-col">
										{/* Image */}
										<div className="relative h-24 sm:h-32 w-full overflow-hidden rounded-lg mb-2 sm:mb-3">
											<Image
												src={item.imageUrl}
												alt={item.title}
												fill
												className="object-cover group-hover:scale-[1.02] transition-transform duration-300"
											/>
										</div>

										{/* Content */}
										<div className="flex-1 flex flex-col">
											<h3 className="text-white text-xs sm:text-sm font-semibold mb-1 sm:mb-2 group-hover:text-ss-light-green transition-colors line-clamp-2">
												{item.title}
											</h3>
											<p className="text-white/70 text-[10px] sm:text-xs leading-relaxed flex-1 line-clamp-3">
												{item.text}
											</p>
											<div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-white/20">
												<span className="text-ss-light-green text-[10px] sm:text-xs font-medium">
													Read more →
												</span>
											</div>
										</div>
									</div>
								</Link>
							))}
						</div>
					</div>
				)}
			</section>
		);
	}

	// Regular version
	return (
		<>
			<div className="relative overflow-visible">
				<section className={`py-2 sm:py-4 md:py-8 bg-linear-to-l from-ss-light-green via-ss-green to-ss-blue rounded-2xl sm:rounded-3xl overflow-visible ${className}`} style={{ overflow: "visible" }}>
					<div className="max-w-7xl mx-auto px-6 lg:px-8 overflow-visible">
						<div className="grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr] /gap-6 /sm:gap-8 /md:gap-12 /lg:gap-16 items-center overflow-visible">
							{/* Left Content */}
							<div className="order-2 lg:order-1 lg:pr-8">
								<div className="text-white">
									<h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold mb-4 sm:mb-6 leading-tight text-white">
										{title || "Empower Your Students to Succeed"}
									</h2>

									<p className="text-sm sm:text-base md:text-lg lg:text-xl mb-6 sm:mb-8 pb-2 sm:pb-4 leading-relaxed opacity-90 text-white">
										{description || `Give your students the ${t("general.words.personalised")} support they need to thrive. StudyStash helps boost engagement, confidence, and outcomes-schedule a demo to see it in action.`}
									</p>

									{/* Buttons */}
									<div className="flex flex-row flex-wrap gap-3 sm:gap-4 pb-2 sm:pb-4">
										{showSingleButton ? (
											<Link href={buttonHref} className="inline-flex">
												<CustomButton variant="primary" size="lg">
													{buttonText}
												</CustomButton>
											</Link>
										) : (
											<>
												<Link href={buttonHref} className="inline-flex">
													<CustomButton variant="primary" size="md">
														{buttonText}
													</CustomButton>
												</Link>
												{secondButtonText && secondButtonHref ? (
													<Link href={secondButtonHref} className="inline-flex">
														<CustomButton variant="ghost" size="md">
															{secondButtonText}
														</CustomButton>
													</Link>
												) : (
													<CustomButton
														variant="ghost"
														size="md"
														onClick={() => setIsModalOpen(true)}
														className="inline-flex"
													>
														Download the report
													</CustomButton>
												)}
											</>
										)}
									</div>
								</div>
							</div>

							{/* Right Content - Study Preview or Pagination */}
							<div className="relative w-full order-1 lg:order-2 overflow-visible flex justify-center lg:justify-end">
								{showImage && !showPagination ? (
									<div className="w-full max-w-sm sm:max-w-md md:max-w-lg lg:max-w-xl transition-all duration-500 ease-in-out relative overflow-visible">
										<div className="relative z-50 flex items-center justify-center">
											<Image
												src={imageSrc || "/Brochure-Mockup.png"}
												alt={imageAlt || "StudyStash Research Study"}
												width={1600}
												height={1160}
												className="w-full h-auto drop-shadow-2xl transition-all duration-500 ease-in-out"
											/>
										</div>
									</div>
								) : showPagination ? (
									<div className="flex items-center justify-center h-full p-4">
										{/* Pagination Controls */}
										<div className="flex items-center gap-3 sm:gap-4 md:gap-6">
											<button
												onClick={handlePrevPage}
												disabled={currentPage === 0}
												className="p-2 sm:p-3 md:p-4 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 text-white hover:bg-white/30 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
												aria-label="Previous page"
											>
												<ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8" />
											</button>

											<div className="text-white text-center">
												<div className="text-sm sm:text-base md:text-lg font-semibold">
													Page {currentPage + 1} of {totalPages}
												</div>
												<div className="text-xs sm:text-sm opacity-80 hidden sm:block">
													Showing {currentItems.length} of {caseStudies.length} case studies
												</div>
											</div>

											<button
												onClick={handleNextPage}
												disabled={currentPage === totalPages - 1}
												className="p-2 sm:p-3 md:p-4 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 text-white hover:bg-white/30 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
												aria-label="Next page"
											>
												<ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8" />
											</button>
										</div>
									</div>
								) : null}
							</div>
						</div>

						{/* Case Studies Display */}
						{showPagination && currentItems.length > 0 && (
							<div className="mt-8 sm:mt-12 md:mt-16 px-4 sm:px-6 lg:px-8">
								<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
									{currentItems.map((item, index) => (
										<Link
											key={index}
											href={item.link}
											className="group block h-full"
										>
											<div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 sm:p-6 transition-all duration-300 ease-in-out group-hover:scale-[1.02] group-hover:bg-white/20 h-full flex flex-col">
												{/* Image */}
												<div className="relative h-32 sm:h-40 md:h-48 w-full overflow-hidden rounded-xl mb-3 sm:mb-4">
													<Image
														src={item.imageUrl}
														alt={item.title}
														fill
														className="object-cover group-hover:scale-[1.02] transition-transform duration-300"
													/>
												</div>

												{/* Content */}
												<div className="flex-1 flex flex-col">
													<h3 className="text-white text-sm sm:text-base md:text-lg font-semibold mb-2 sm:mb-3 group-hover:text-ss-light-green transition-colors">
														{item.title}
													</h3>
													<p className="text-white/80 text-xs sm:text-sm leading-relaxed flex-1">
														{item.text}
													</p>
													<div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-white/20">
														<span className="text-ss-light-green text-xs sm:text-sm font-medium">
															Read more →
														</span>
													</div>
												</div>
											</div>
										</Link>
									))}
								</div>
							</div>
						)}
					</div>
				</section>
			</div>
			<DownloadFormModal
				open={isModalOpen}
				onOpenChange={setIsModalOpen}
			/>
		</>
	);
}
